import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import { PUBLIC_BASE } from './config';
import PublicLayout from '../../components/public/PublicLayout';

export default function PublicClientReviewModifyPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { me } = useSelector(authSelector.selectAuthState);
  const currentPage = parseInt(new URLSearchParams(location.search).get('page')) || 1;

  const isAdmin = me?.reviewAdminYn === 'Y' || me?.adminYn === 'Y' || me?.superAdminYn === 'Y';

  const [loading, setLoading] = useState(false);
  const [album, setAlbum] = useState(null);
  const [client, setClient] = useState(null);
  const [physicalAlbumYn, setPhysicalAlbumYn] = useState('N');
  const [songs, setSongs] = useState([]);

  // 가사 모달
  const [lyricsModalOpen, setLyricsModalOpen] = useState(false);
  const [currentSongIdx, setCurrentSongIdx] = useState(null);
  const [tempLyrics, setTempLyrics] = useState('');
  const [tempLyricsTranslation, setTempLyricsTranslation] = useState('');
  const [hasTranslation, setHasTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/public/reviewApply/client-apply/${id}`);
        const { album: al, client: cl, songs: sl } = res.data;
        setAlbum(al);
        setClient(cl);
        setPhysicalAlbumYn(al?.physicalAlbumYn || 'N');
        setSongs(sl.map((s) => ({
          songId: s.id,
          nameKr: s.nameKr || '',
          track: s.track,
          featuring: s.featuring || '',
          featuringLatestRelease: s.featuringLatestRelease || '',
          performance: s.performance || '',
          lyrics: s.lyrics || '',
          lyricsTranslation: s.lyricsTranslation || '',
          woneumYn: s.woneumYn === 'Y',
        })));
      } catch (e) {
        console.error(e);
        alert('데이터 불러오기 실패');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isAdmin]);

  useEffect(() => {
    if (!lyricsModalOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') closeLyricsModal(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lyricsModalOpen]);

  const updateSong = (idx, field, value) => {
    setSongs((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const openLyricsModal = (idx) => {
    setCurrentSongIdx(idx);
    setTempLyrics(songs[idx]?.lyrics || '');
    const trans = songs[idx]?.lyricsTranslation || '';
    setTempLyricsTranslation(trans);
    setHasTranslation(!!trans);
    setLyricsModalOpen(true);
  };
  const closeLyricsModal = () => {
    setLyricsModalOpen(false); setCurrentSongIdx(null);
    setTempLyrics(''); setTempLyricsTranslation(''); setHasTranslation(false); setTranslating(false);
  };
  const autoTranslate = async () => {
    if (!tempLyrics.trim()) return;
    setHasTranslation(true);
    setTranslating(true);
    try {
      const res = await axios.post('/api/public/reviewApply/translate-lyrics', { lyrics: tempLyrics });
      setTempLyricsTranslation(res.data.translation || '');
    } catch (e) {
      alert('번역 중 오류가 발생했습니다.');
    } finally {
      setTranslating(false);
    }
  };
  const saveLyricsModal = () => {
    if (currentSongIdx !== null) {
      updateSong(currentSongIdx, 'lyrics', tempLyrics);
      updateSong(currentSongIdx, 'lyricsTranslation', hasTranslation ? tempLyricsTranslation : '');
    }
    closeLyricsModal();
  };

  const canSubmit = useMemo(() => songs.every((s) => {
    if (!s.performance?.trim()) return false;
    if (!(s.nameKr || '').includes('Inst.') && !s.lyrics?.trim()) return false;
    return true;
  }), [songs]);

  const onSubmit = async () => {
    if (!canSubmit) { alert('실연 및 가사 항목을 확인해 주세요.'); return; }
    try {
      const payload = {
        physicalAlbumYn,
        songs: songs.map((s) => ({
          songId: s.songId,
          featuring: s.featuring?.trim(),
          featuringLatestRelease: s.featuringLatestRelease?.trim(),
          performance: s.performance?.trim(),
          lyrics: s.lyrics?.trim(),
          lyricsTranslation: s.lyricsTranslation?.trim() || null,
          woneumYn: s.woneumYn || false,
        })),
      };
      await axios.put(`/api/public/reviewApply/client-apply/${id}`, payload);
      alert('수정되었습니다.');
      navigate(`${PUBLIC_BASE}/client-review/${id}?page=${currentPage}`);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || '수정 중 오류가 발생했습니다.');
    }
  };

  if (!isAdmin) return (
    <PublicLayout>
      <div className="py-20 text-center text-slate-500">권한이 없습니다.</div>
    </PublicLayout>
  );

  if (loading) return (
    <PublicLayout>
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3" />
        <span className="text-sm text-slate-500">불러오는 중...</span>
      </div>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <h1 className="text-xl sm:text-2xl font-bold text-white text-center mb-8">신청서 수정 (권리사)</h1>

        <div className="space-y-6 max-w-3xl mx-auto">
          {/* 앨범 정보 (읽기전용) */}
          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-3 bg-[#182334]">
            <h3 className="text-sm font-semibold text-slate-400 mb-2">앨범 정보 (읽기전용)</h3>
            <ReadField label="앨범명" value={album?.nameKr} />
            <ReadField label="아티스트" value={album?.artist?.nameKr} />
            <ReadField label="권리사" value={client?.name} />
          </div>

          {/* 피지컬 여부 */}
          <div className="border border-white/[0.08] rounded-2xl p-4 bg-[#182334]">
            <label className="flex items-center gap-3 cursor-pointer">
              <span className="text-sm text-slate-300">피지컬 앨범 여부</span>
              <select value={physicalAlbumYn} onChange={(e) => setPhysicalAlbumYn(e.target.value)}
                className="px-3 py-1.5 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300">
                <option value="N">N</option>
                <option value="Y">Y</option>
              </select>
            </label>
          </div>

          {/* 구분선 */}
          <div className="flex items-center my-4">
            <div className="flex-grow border-t border-white/[0.08]" />
            <span className="px-3 text-slate-300 font-bold">곡 정보</span>
            <div className="flex-grow border-t border-white/[0.08]" />
          </div>

          {/* 곡 리스트 */}
          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-6 bg-white/[0.03]">
            {songs.map((s, idx) => (
              <div key={idx} className="rounded-xl border border-white/[0.08] p-4 space-y-3 bg-[#182334]">
                <div className="text-sm font-semibold text-slate-300">
                  {String(s.track || '').padStart(2, '0')}. {s.nameKr}
                </div>

                <InputField label="피처링" value={s.featuring} onChange={(e) => updateSong(idx, 'featuring', e.target.value)} />
                <InputField label="피처링 아티스트의 최근 음원" value={s.featuringLatestRelease} onChange={(e) => updateSong(idx, 'featuringLatestRelease', e.target.value)} />
                <InputField label="실연(연주자명/악기명)" required value={s.performance} onChange={(e) => updateSong(idx, 'performance', e.target.value)} />

                {!s.nameKr.includes('Inst.') && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                      가사 <span className="text-red-500">*</span>
                    </label>
                    <button
                      onClick={() => openLyricsModal(idx)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                        s.lyrics ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30'
                      }`}>
                      {s.lyrics ? '가사 수정' : '가사 입력'}
                    </button>
                  </div>
                )}

                {/* 원음방송 */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={s.woneumYn} onChange={(e) => updateSong(idx, 'woneumYn', e.target.checked)} className="peer hidden" />
                  <span className="w-6 h-6 flex items-center justify-center border border-white/[0.08] rounded-full peer-checked:bg-sky-500 peer-checked:border-sky-500 text-white text-sm">✓</span>
                  <span className="text-sm font-semibold text-slate-200">원음방송 선택</span>
                </label>
              </div>
            ))}
          </div>

          {/* 버튼 */}
          <div className="flex flex-wrap justify-end items-center gap-2 mt-6">
            <button onClick={onSubmit}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                canSubmit ? 'bg-sky-600 text-white hover:bg-sky-500' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
              }`}>
              수정완료
            </button>
            <button onClick={() => navigate(`${PUBLIC_BASE}/client-review/${id}?page=${currentPage}`)}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
              취소
            </button>
          </div>
        </div>
      </div>

      {/* 가사 모달 */}
      {lyricsModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50" onClick={closeLyricsModal}>
          <div className="bg-[#182334] rounded-2xl p-6 w-3/4 h-3/4 flex flex-col shadow-xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-white mb-2">가사 입력</h2>
            <p className="text-sm text-rose-400 mb-2">※ 영문 가사의 경우 <b>의역이 필수</b>입니다.</p>
            <textarea
              value={tempLyrics}
              onChange={(e) => setTempLyrics(e.target.value)}
              className={`w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none ${hasTranslation ? 'flex-none h-2/5' : 'flex-1'}`}
              placeholder="곡의 전체 가사를 입력해 주세요."
            />
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <input type="checkbox" id="hasTranslationCheck" checked={hasTranslation} onChange={(e) => setHasTranslation(e.target.checked)} className="w-4 h-4 accent-sky-400" />
              <label htmlFor="hasTranslationCheck" className="text-sm text-slate-300 cursor-pointer">한글번역 포함</label>
              <button onClick={autoTranslate} disabled={translating || !tempLyrics.trim()}
                className="px-3 py-1 rounded-lg text-xs font-semibold bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {translating ? '번역 중...' : '✦ 자동번역'}
              </button>
            </div>
            {hasTranslation && (
              <textarea
                value={translating ? '번역 중...' : tempLyricsTranslation}
                onChange={(e) => setTempLyricsTranslation(e.target.value)}
                readOnly={translating}
                className="flex-1 w-full mt-2 px-3 py-2 rounded-xl border border-sky-500/30 bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none"
                placeholder="한글번역 가사를 입력해 주세요."
              />
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={closeLyricsModal} className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">취소</button>
              <button onClick={saveLyricsModal} className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white hover:bg-sky-500 transition-colors">저장</button>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

function ReadField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
      <div className="px-3 py-2 rounded-xl border border-white/[0.05] bg-white/5 text-sm text-slate-400">{value || '-'}</div>
    </div>
  );
}

function InputField({ label, value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text" value={value} onChange={onChange} placeholder={placeholder}
        className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent"
      />
    </div>
  );
}
